import { useEffect, useState, useMemo, useCallback } from "react";
import Badge from "../components/Badge";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import PageLayout from "../components/PageLayout";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import Tooltip from "../components/Tooltip";
import {
  buttonStyle,
  buttonBackgroundColor,
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  emptyRowStyle,
} from "../components/Style";
import { apiClient } from "../services/api";
import { type Compensation } from "../types/Compensation";
import { renderClassBadges } from "../utils/classBadgeUtils";
import { GRADE_FILTER_OPTIONS } from "../components/Grade";

type CompensationRow = Compensation & {
  supplementClassName?: string;
};

const columnHelper = createColumnHelper<CompensationRow>();

export default function CompensationManagementPage() {
  const [compensations, setCompensations] = useState<CompensationRow[]>([]);
  const [classes, setClasses] = useState<any[]>([]); // 보강반만 (드롭다운 선택용)
  const [allClasses, setAllClasses] = useState<any[]>([]); // 전체 반 (배지 색상용)
  const [editingCell, setEditingCell] = useState<{ id: number; field: string } | null>(null);
  // State: Sort & Filters for TanStack Table
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [compensationList, classList] = await Promise.all([
        apiClient.getCompensations(),
        apiClient.getClasses(),
      ]);

      // 보강반 이름 매핑
      const enriched = compensationList.map((comp: Compensation) => ({
        ...comp,
        supplementClassName:
          comp.supplementClassId
            ? classList.find((c: any) => c.id === comp.supplementClassId)?.className
            : null,
      }));

      setCompensations(enriched);
      setAllClasses(classList);
      setClasses(classList.filter((c: any) => c.classCategory === 'supplement'));
    } catch (error) {
      console.error("데이터 불러오기 실패:", error);
    }
  };

  const handleSync = async () => {
    try {
      await apiClient.syncCompensations();
      await loadData();
    } catch (error) {
      console.error("동기화 실패:", error);
    }
  };

  const handleUpdate = useCallback(async (id: number, field: string, value: any) => {
    try {
      const updateData: any = {};
      
      if (field === 'compensationType') {
        updateData.compensationType = value;
        // 타입 변경 시 관련 필드 초기화
        if (value !== 'MAKEUP') updateData.supplementClassId = null;
        if (value !== 'CREDIT') updateData.creditAmount = null;
        if (value !== 'NONE') updateData.detailReason = null;
      } else if (field === 'supplementClassId') {
        updateData.supplementClassId = value ? parseInt(value) : null;
      } else if (field === 'creditAmount') {
        updateData.creditAmount = value ? parseInt(value) : null;
      } else if (field === 'detailReason') {
        updateData.detailReason = value;
      } else if (field === 'isCompleted') {
        updateData.isCompleted = value;
      }

      await apiClient.updateCompensation(id, updateData);
      await loadData();
      setEditingCell(null);
    } catch (error) {
      console.error("수정 실패:", error);
    }
  }, []);

  const classOptions = useMemo(() => {
    if (classes.length === 0) {
      const set = new Set(compensations.map((c) => c.class).filter(Boolean));
      return Array.from(set).sort();
    }
    return Array.from(new Set(classes.map((c) => c.className))).sort();
  }, [classes, compensations]);

  const renderCompensationType = useCallback((row: CompensationRow) => {
    const isEditing = editingCell?.id === row.id && editingCell?.field === 'compensationType';
    
    const typeLabels: Record<string, string> = {
      MAKEUP: '보강',
      CREDIT: '차감',
      NONE: '없음',
    };

    if (isEditing) {
      return (
        <select
          autoFocus
          value={row.compensationType}
          onChange={(e) => handleUpdate(row.id, 'compensationType', e.target.value)}
          onBlur={() => setEditingCell(null)}
          style={{
            padding: "4px 8px",
            borderRadius: 6,
            border: "1px solid #0969da",
            fontSize: 13,
            width: "100%",
          }}
        >
          <option value="MAKEUP">보강</option>
          <option value="CREDIT">차감</option>
          <option value="NONE">없음</option>
        </select>
      );
    }

    const badgeProps = row.compensationType === 'MAKEUP' 
      ? { backgroundColor: '#ddf4ff', textColor: '#0969da', borderColor: '#b6e3ff' }
      : row.compensationType === 'CREDIT'
      ? { backgroundColor: '#fff1ed', textColor: '#d1242f', borderColor: '#fca5a5' }
      : { backgroundColor: '#f6f8fa', textColor: '#656d76', borderColor: '#d1d5db' };

    return (
      <Tooltip content="더블클릭하여 수정">
        <Badge
          {...badgeProps}
          onDoubleClick={() => setEditingCell({ id: row.id, field: 'compensationType' })}
        >
          {typeLabels[row.compensationType] || row.compensationType}
        </Badge>
      </Tooltip>
    );
  }, [editingCell, handleUpdate]);

  const renderCompensationDetail = useCallback((row: CompensationRow) => {
    if (row.compensationType === 'MAKEUP') {
      const isEditing = editingCell?.id === row.id && editingCell?.field === 'supplementClassId';
      
      if (isEditing) {
        return (
          <select
            autoFocus
            value={row.supplementClassId || ''}
            onChange={(e) => handleUpdate(row.id, 'supplementClassId', e.target.value)}
            onBlur={() => setEditingCell(null)}
            style={{
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #0969da",
              fontSize: 13,
              width: "100%",
            }}
          >
            <option value="">선택하세요</option>
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.className}
              </option>
            ))}
          </select>
        );
      }

      return (
        <Tooltip content="더블클릭하여 수정">
          <span
            onDoubleClick={() => setEditingCell({ id: row.id, field: 'supplementClassId' })}
            style={{ cursor: "pointer", fontSize: 13, color: row.supplementClassId ? '#24292f' : '#656d76' }}
          >
            {row.supplementClassName || '(선택 필요)'}
          </span>
        </Tooltip>
      );
    } else if (row.compensationType === 'CREDIT') {
      const isEditing = editingCell?.id === row.id && editingCell?.field === 'creditAmount';
      
      if (isEditing) {
        return (
          <input
            autoFocus
            type="number"
            defaultValue={row.creditAmount || ''}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = (e.target as HTMLInputElement).value;
                handleUpdate(row.id, 'creditAmount', value);
              } else if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            onBlur={() => setEditingCell(null)}
            style={{
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #0969da",
              fontSize: 13,
              width: "100%",
            }}
            placeholder="금액 입력 후 Enter"
            min="0"
          />
        );
      }

      return (
        <Tooltip content="더블클릭하여 수정">
          <span
            onDoubleClick={() => setEditingCell({ id: row.id, field: 'creditAmount' })}
            style={{ cursor: "pointer", fontSize: 13, color: row.creditAmount ? '#24292f' : '#656d76' }}
          >
            {row.creditAmount ? `${row.creditAmount.toLocaleString()}원` : '(금액 입력)'}
          </span>
        </Tooltip>
      );
    } else if (row.compensationType === 'NONE') {
      const isEditing = editingCell?.id === row.id && editingCell?.field === 'detailReason';
      
      if (isEditing) {
        return (
          <select
            autoFocus
            value={row.detailReason || ''}
            onChange={(e) => handleUpdate(row.id, 'detailReason', e.target.value)}
            onBlur={() => setEditingCell(null)}
            style={{
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #0969da",
              fontSize: 13,
              width: "100%",
            }}
          >
            <option value="">선택하세요</option>
            <option value="학부모가 원치 않음">학부모가 원치 않음</option>
            <option value="규정 상 소멸">규정 상 소멸</option>
          </select>
        );
      }

      return (
        <Tooltip content="더블클릭하여 수정">
          <span
            onDoubleClick={() => setEditingCell({ id: row.id, field: 'detailReason' })}
            style={{ cursor: "pointer", fontSize: 13, color: row.detailReason ? '#24292f' : '#656d76' }}
          >
            {row.detailReason || '(사유 선택)'}
          </span>
        </Tooltip>
      );
    }

    return <span style={{ fontSize: 13, color: '#656d76' }}>-</span>;
  }, [editingCell, classes, handleUpdate]);

  // TanStack Table 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("studentNumber", {
      header: "출결번호",
      size: 100,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("name", {
      header: "학생이름",
      size: 100,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("grade", {
      header: "학년",
      size: 80,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("class", {
      header: "반",
      size: 200,
      cell: (info) => renderClassBadges(info.getValue(), allClasses),
      sortingFn: "alphanumeric",
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const classString = row.getValue(columnId) as string;
        return classString.includes(filterValue);
      },
    }),
    columnHelper.accessor("absenceDate", {
      header: "결석한 날짜",
      size: 160,
      cell: (info) => {
        const date = new Date(info.getValue());
        return date.toLocaleString('ko-KR', { 
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Seoul'
        });
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor("compensationType", {
      header: "보상 형식",
      size: 120,
      cell: (info) => renderCompensationType(info.row.original),
      enableColumnFilter: false,
    }),
    columnHelper.display({
      id: "compensationDetail",
      header: "보상 내용",
      size: 200,
      cell: (info) => renderCompensationDetail(info.row.original),
    }),
    columnHelper.accessor("isCompleted", {
      header: "보상 완료",
      size: 100,
      cell: (info) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <input
            type="checkbox"
            checked={info.getValue()}
            onChange={(e) => handleUpdate(info.row.original.id, 'isCompleted', e.target.checked)}
            style={{
              width: 18,
              height: 18,
              cursor: "pointer",
            }}
          />
        </div>
      ),
      enableColumnFilter: false,
    }),
  ], [renderCompensationType, renderCompensationDetail, handleUpdate]); // handleUpdate 의존성 추가

  // Table instance
  const table = useReactTable({
    data: compensations,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout title="결석 보상 관리">
      <div style={{ marginBottom: 16 }}>
        <Tooltip content="출결 이력을 참조하여 보상 레코드를 자동으로 생성합니다.">
          <button
            onClick={handleSync}
            style={{
              ...buttonStyle,
              ...buttonBackgroundColor.primary,
            }}
          >
            결석 기록 동기화
          </button>
        </Tooltip>
      </div>

      <p style={{ fontSize: 13, color: '#656d76', marginBottom: 12 }}>
        💡 더블클릭하여 보상 형식, 보상 내용을 수정할 수 있습니다.
      </p>

      <style>
        {`
          .compensation-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="compensation-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          {header.column.getCanFilter() && (
                            <div style={filterContainerStyle}>
                              {columnId === "studentNumber" && (
                                <TableInput
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                                  placeholder="검색"
                                />
                              )}
                              {columnId === "name" && (
                                <TableInput
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                                  placeholder="검색"
                                />
                              )}
                              {columnId === "grade" && (
                                <Dropdown
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(value) => header.column.setFilterValue(value || undefined)}
                                  options={[
                                    { value: "", label: "전체" },
                                    ...GRADE_FILTER_OPTIONS,
                                  ]}
                                />
                              )}
                              {columnId === "class" && (
                                <Dropdown
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(value) => header.column.setFilterValue(value || undefined)}
                                  options={[
                                    { value: "", label: "전체" },
                                    ...classOptions.map((c) => ({ value: c, label: c })),
                                  ]}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  보상 기록이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
